import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Crown, Buildings, ChartBar, Users, SignOut, List, X, ArrowLeft, CurrencyEur, ShieldCheck, Scales } from '@phosphor-icons/react';
import SystemNoticeBanner from './SystemNoticeBanner';
import LegalFooter from './LegalFooter';

export default function SuperAdminLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/super-admin', icon: ChartBar, label: 'Übersicht', end: true },
    { to: '/super-admin/mandanten', icon: Buildings, label: 'Mandanten' },
    { to: '/super-admin/ki-abrechnung', icon: CurrencyEur, label: 'KI-Abrechnung' },
    { to: '/super-admin/sicherheits-audit', icon: ShieldCheck, label: 'Sicherheits-Audit' },
    { to: '/super-admin/compliance', icon: Scales, label: 'Lizenz-Compliance' },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="super-admin-layout">
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-violet-900 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Crown weight="fill" className="w-6 h-6 text-amber-400" />
          <span className="font-heading text-base font-black tracking-tight">SUPER-ADMIN</span>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-violet-800 rounded-lg" data-testid="sa-menu-toggle">
          {sidebarOpen ? <X weight="bold" className="w-6 h-6" /> : <List weight="bold" className="w-6 h-6" />}
        </button>
      </header>

      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen z-50 bg-violet-900 text-white flex flex-col transition-transform duration-300 w-72 lg:w-64
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
        data-testid="sa-sidebar"
      >
        <div className="hidden lg:flex p-6 border-b border-violet-800 items-center gap-3">
          <Crown weight="fill" className="w-7 h-7 text-amber-400 flex-shrink-0" />
          <div>
            <span className="font-heading text-base font-black tracking-tight block">SUPER</span>
            <span className="font-heading text-base font-black tracking-tight block">ADMIN</span>
          </div>
        </div>

        <div className="lg:hidden flex items-center justify-between p-4 border-b border-violet-800">
          <span className="font-heading text-lg font-bold">Menü</span>
          <button onClick={() => setSidebarOpen(false)} className="p-2 text-violet-200 hover:text-white">
            <X weight="bold" className="w-6 h-6" />
          </button>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3.5 transition-colors rounded-lg ${
                  isActive ? 'bg-violet-800 text-white' : 'text-violet-200 hover:text-white hover:bg-violet-800'
                }`
              }
              data-testid={`sa-nav-${item.label.toLowerCase()}`}
            >
              <item.icon weight="bold" className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium">{item.label}</span>
            </NavLink>
          ))}

          <div className="pt-4 mt-4 border-t border-violet-800">
            <NavLink
              to="/"
              className="flex items-center gap-3 px-4 py-3 text-violet-200 hover:text-white hover:bg-violet-800 rounded-lg transition-colors"
              data-testid="sa-nav-back-to-admin"
            >
              <ArrowLeft weight="bold" className="w-5 h-5 flex-shrink-0" />
              <span className="font-medium text-sm">Zur Mandanten-Ansicht</span>
            </NavLink>
          </div>
        </nav>

        <div className="p-4 border-t border-violet-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-amber-400 text-violet-900 flex items-center justify-center font-bold rounded-lg">
              {user?.name?.charAt(0).toUpperCase() || 'S'}
            </div>
            <div className="overflow-hidden flex-1">
              <p className="font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-violet-200 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-violet-200 hover:text-white hover:bg-violet-800 transition-colors rounded-lg"
            data-testid="sa-logout-button"
          >
            <SignOut weight="bold" className="w-5 h-5" />
            <span className="font-medium">Abmelden</span>
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen flex flex-col min-w-0">
        <div className="sticky top-0 z-30 lg:top-0 mt-16 lg:mt-0">
          <SystemNoticeBanner />
        </div>
        <div className="p-4 sm:p-6 lg:p-8 flex-1 min-w-0 overflow-x-hidden">{children}</div>
        <LegalFooter />
      </main>
    </div>
  );
}
